/**
 * Media Selector Lightbox Component
 * Handles lightbox functionality for image galleries, videos, and virtual tours
 */

interface Gallery {
  element: HTMLElement;
  totalImages: number;
  currentIndex: number;
  images: string[];
  captions: string[];
}

// State management
let currentMediaType = 0;
const galleries: Gallery[] = [];

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

function init(): void {
  const wrapper = document.querySelector<HTMLElement>('.media-selector-wrapper');

  if (!wrapper) return;

  const lightbox = wrapper.querySelector<HTMLElement>('.lightbox');

  if (!lightbox) return;

  // Initialize gallery data
  initializeGalleries(wrapper);

  // Bottom navigation buttons - open lightbox
  const bottomNavButtons = wrapper.querySelectorAll<HTMLButtonElement>('.media-nav-btns button');

  bottomNavButtons.forEach((button, index) => {
    button.addEventListener('click', () => {
      openLightbox(lightbox, index);
      switchMediaType(wrapper, index);
    });
  });

  // Hero image click - open lightbox
  const heroImage = wrapper.querySelector<HTMLElement>('.img-wrapper');

  if (heroImage) {
    heroImage.addEventListener('click', () => {
      openLightbox(lightbox, 0);
    });
  }

  // Close button
  const closeButton = lightbox.querySelector<HTMLButtonElement>('.lightbox-actions button');

  if (closeButton) {
    closeButton.addEventListener('click', () => {
      closeLightbox(lightbox);
    });
  }

  // Lightbox tab navigation
  const tabButtons = lightbox.querySelectorAll<HTMLButtonElement>('.lightbox-nav-wrapper .nav-item-wrapper');

  tabButtons.forEach((button, index) => {
    button.addEventListener('click', () => {
      switchMediaType(wrapper, index);
    });
  });

  // Gallery navigation
  initializeGalleryNavigation(wrapper);

  // Keyboard navigation
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (!lightbox.classList.contains('open')) return;

    switch (e.key) {
      case 'Escape':
        closeLightbox(lightbox);
        break;
      case 'ArrowLeft':
        navigateGallery(wrapper, currentMediaType, -1);
        break;
      case 'ArrowRight':
        navigateGallery(wrapper, currentMediaType, 1);
        break;
    }
  });

  // Close on backdrop click
  lightbox.addEventListener('click', (e: MouseEvent) => {
    if (e.target === lightbox) {
      closeLightbox(lightbox);
    }
  });
}

/**
 * Initialize gallery data from DOM attributes
 * Parses JSON data attributes for images and captions
 */
function initializeGalleries(wrapper: HTMLElement): void {
  const galleryElements = wrapper.querySelectorAll<HTMLElement>('.lightbox-body .gallery-full[data-media-index]');

  galleryElements.forEach((element) => {
    const mediaIndex = parseInt(element.getAttribute('data-media-index') || '0');
    const imagesData = element.getAttribute('data-images');
    const captionsData = element.getAttribute('data-captions');

    let images: string[] = [];
    let captions: string[] = [];

    try {
      images = imagesData ? JSON.parse(imagesData) : [];
      captions = captionsData ? JSON.parse(captionsData) : [];
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error parsing gallery data:', e);
    }

    galleries[mediaIndex] = {
      element: element,
      totalImages: images.length,
      currentIndex: 0,
      images: images,
      captions: captions,
    };
  });
}

function initializeGalleryNavigation(wrapper: HTMLElement): void {
  const galleryElements = wrapper.querySelectorAll<HTMLElement>('.lightbox-body [data-media-index]');

  galleryElements.forEach((gallery) => {
    const mediaIndex = parseInt(gallery.getAttribute('data-media-index') || '0');

    // Large chevron buttons
    const largeLeftChevron = gallery.querySelector<HTMLButtonElement>('.image-chevron-wrapper .chevron-left button');
    const largeRightChevron = gallery.querySelector<HTMLButtonElement>('.image-chevron-wrapper .chevron-right button');

    if (largeLeftChevron) {
      largeLeftChevron.addEventListener('click', () => {
        navigateGallery(wrapper, mediaIndex, -1);
      });
    }

    if (largeRightChevron) {
      largeRightChevron.addEventListener('click', () => {
        navigateGallery(wrapper, mediaIndex, 1);
      });
    }

    // Small chevron buttons in footer
    const smallLeftChevron = gallery.querySelector<HTMLButtonElement>('.image-footer .chevron-left button');
    const smallRightChevron = gallery.querySelector<HTMLButtonElement>('.image-footer .chevron-right button');

    if (smallLeftChevron) {
      smallLeftChevron.addEventListener('click', () => {
        navigateGallery(wrapper, mediaIndex, -1);
      });
    }

    if (smallRightChevron) {
      smallRightChevron.addEventListener('click', () => {
        navigateGallery(wrapper, mediaIndex, 1);
      });
    }

    // Thumbnail buttons
    const thumbnails = gallery.querySelectorAll<HTMLButtonElement>('.thumb');

    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        jumpToImage(wrapper, mediaIndex, index);
      });
    });
  });

  // Video play button
  const videoWrappers = wrapper.querySelectorAll<HTMLElement>('.video-component-wrapper[data-media-index]');

  videoWrappers.forEach((videoWrapper) => {
    const playButton = videoWrapper.querySelector<HTMLElement>('.play-button');
    const placeholder = videoWrapper.querySelector<HTMLElement>('.video-placeholder');
    const iframe = videoWrapper.querySelector<HTMLIFrameElement>('iframe');

    if (playButton && placeholder && iframe) {
      playButton.addEventListener('click', () => {
        placeholder.classList.add('d-none');
        iframe.classList.remove('d-none');
        // Add autoplay parameter if not already present
        if (iframe.src && !iframe.src.includes('autoplay=1')) {
          iframe.src = iframe.src + (iframe.src.includes('?') ? '&' : '?') + 'autoplay=1';
        }
      });
    }
  });
}

function openLightbox(lightbox: HTMLElement, mediaIndex: number): void {
  lightbox.classList.remove('closed');
  lightbox.classList.add('open');
  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';
  currentMediaType = mediaIndex;

  // Preload adjacent images when lightbox opens
  const gallery = galleries[mediaIndex];

  if (gallery) {
    preloadAdjacentImages(mediaIndex, gallery.currentIndex);
  }
}

function closeLightbox(lightbox: HTMLElement): void {
  lightbox.classList.remove('open');
  lightbox.classList.add('closed');
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';

  // Reset all videos
  const wrapper = lightbox.closest<HTMLElement>('.media-selector-wrapper');

  if (wrapper) {
    resetAllVideos(wrapper);

    // Remove active class from bottom nav buttons
    const bottomNavButtons = wrapper.querySelectorAll<HTMLButtonElement>('.media-nav-btns button');

    bottomNavButtons.forEach((btn) => btn.classList.remove('active'));
  }
}

function switchMediaType(wrapper: HTMLElement, mediaIndex: number): void {
  currentMediaType = mediaIndex;

  // Update tab navigation
  const tabButtons = wrapper.querySelectorAll<HTMLButtonElement>('.lightbox-nav-wrapper .nav-item-wrapper');

  tabButtons.forEach((btn, idx) => {
    const textElement = btn.querySelector<HTMLElement>('.nav-item-text');

    if (textElement) {
      if (idx === mediaIndex) {
        textElement.classList.add('active');
        btn.classList.add('active');
      } else {
        textElement.classList.remove('active');
        btn.classList.remove('active');
      }
    }
  });

  // Update bottom navigation
  const bottomButtons = wrapper.querySelectorAll<HTMLButtonElement>('.media-nav-btns button');

  bottomButtons.forEach((btn, idx) => {
    if (idx === mediaIndex) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }
  });

  // Show/hide content
  const contentSections = wrapper.querySelectorAll<HTMLElement>('.lightbox-body [data-media-index]');

  contentSections.forEach((section) => {
    const sectionIndex = parseInt(section.getAttribute('data-media-index') || '0');

    if (sectionIndex === mediaIndex) {
      section.classList.remove('d-none');
    } else {
      section.classList.add('d-none');
      // Reset video if switching away from it
      if (section.classList.contains('video-component-wrapper')) {
        resetVideo(section);
      }
    }
  });
}

function resetVideo(videoWrapper: HTMLElement): void {
  const placeholder = videoWrapper.querySelector<HTMLElement>('.video-placeholder');
  const iframe = videoWrapper.querySelector<HTMLIFrameElement>('.video-iframe');

  if (placeholder && iframe) {
    placeholder.classList.remove('d-none');
    iframe.classList.add('d-none');
    // Reset iframe src to stop playback
    const src = iframe.src;

    iframe.src = '';
    iframe.src = src.replace(/[?&]autoplay=1/, '');
  }
}

function resetAllVideos(wrapper: HTMLElement): void {
  const videoWrappers = wrapper.querySelectorAll<HTMLElement>('.video-component-wrapper[data-media-index]');

  videoWrappers.forEach(resetVideo);
}

/**
 * Navigate through gallery images
 * @param wrapper - The media selector wrapper element
 * @param mediaIndex - Index of the current media type
 * @param direction - Direction to navigate (-1 for previous, 1 for next)
 */
function navigateGallery(wrapper: HTMLElement, mediaIndex: number, direction: number): void {
  const gallery = galleries[mediaIndex];

  if (!gallery || gallery.totalImages <= 1) return;

  // Calculate new index with circular wrapping
  gallery.currentIndex += direction;
  if (gallery.currentIndex < 0) {
    gallery.currentIndex = gallery.totalImages - 1;
  } else if (gallery.currentIndex >= gallery.totalImages) {
    gallery.currentIndex = 0;
  }

  updateGalleryDisplay(wrapper, mediaIndex);
}

function jumpToImage(wrapper: HTMLElement, mediaIndex: number, imageIndex: number): void {
  const gallery = galleries[mediaIndex];

  if (!gallery) return;

  gallery.currentIndex = imageIndex;
  updateGalleryDisplay(wrapper, mediaIndex);
}

/**
 * Update gallery display with current image
 * Handles image source updates, caption display, thumbnail selection, and chevron states
 * @param wrapper - The media selector wrapper element
 * @param mediaIndex - Index of the current media type
 */
function updateGalleryDisplay(wrapper: HTMLElement, mediaIndex: number): void {
  const gallery = galleries[mediaIndex];

  if (!gallery) return;

  const galleryElement = gallery.element;
  const currentIndex = gallery.currentIndex;

  // Update main image - try multiple selectors for robustness
  let img = galleryElement.querySelector<HTMLImageElement>('.image-with-caption .gallery-img');

  if (!img) {
    img = galleryElement.querySelector<HTMLImageElement>('.image-with-caption img');
  }
  if (!img) {
    img = galleryElement.querySelector<HTMLImageElement>('.gallery-img');
  }
  if (!img) {
    img = galleryElement.querySelector<HTMLImageElement>('.image-wrapper img:not(.play-button)');
  }

  if (img && gallery.images[currentIndex]) {
    // Force image update by setting src
    const newSrc = gallery.images[currentIndex];

    img.src = newSrc;

    // Also update srcset if it exists
    if (img.srcset) {
      img.srcset = newSrc;
    }

    // For responsive picture elements, update source elements too
    const picture = img.closest('picture');

    if (picture) {
      const sources = picture.querySelectorAll<HTMLSourceElement>('source');

      sources.forEach((source) => {
        source.srcset = newSrc;
      });
    }
  }

  // Update caption
  const captionElement = galleryElement.querySelector<HTMLParagraphElement>('.image-text');

  if (captionElement) {
    const caption = gallery.captions[currentIndex];

    if (caption) {
      captionElement.textContent = caption;
      captionElement.classList.remove('d-none');
    } else {
      captionElement.classList.add('d-none');
    }
  }

  // Update counter
  const counter = galleryElement.querySelector<HTMLElement>('.counter');

  if (counter) {
    counter.textContent = `${currentIndex + 1}/${gallery.totalImages}`;
  }

  // Update thumbnail selection
  const thumbnails = galleryElement.querySelectorAll<HTMLButtonElement>('.thumb');

  thumbnails.forEach((thumb, idx) => {
    if (idx === currentIndex) {
      thumb.classList.add('selected');
    } else {
      thumb.classList.remove('selected');
    }
  });

  // Scroll thumbnail into view
  if (thumbnails[currentIndex]) {
    thumbnails[currentIndex].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  // Preload adjacent images
  preloadAdjacentImages(mediaIndex, currentIndex);
}

/**
 * Preload adjacent images (current +/- 2) for smoother navigation
 * Removes old preload links and adds new ones for the current position
 * @param mediaIndex - Index of the current media type
 * @param currentIndex - Current image index
 */
function preloadAdjacentImages(mediaIndex: number, currentIndex: number): void {
  const gallery = galleries[mediaIndex];

  if (!gallery || gallery.totalImages <= 1) return;

  // Remove old preload links
  document.querySelectorAll('link[data-media-preload]').forEach((el) => el.remove());

  // Calculate indices to preload: current -2, -1, +1, +2
  const indicesToPreload: number[] = [];

  for (let offset = -2; offset <= 2; offset++) {
    if (offset === 0) continue; // Skip current image (already loaded)

    let index = currentIndex + offset;

    // Handle wraparound
    if (index < 0) {
      index = gallery.totalImages + index;
    } else if (index >= gallery.totalImages) {
      index = index - gallery.totalImages;
    }

    indicesToPreload.push(index);
  }

  // Create preload links for each image
  indicesToPreload.forEach((index) => {
    const imageSrc = gallery.images[index];

    if (imageSrc) {
      const link = document.createElement('link');

      link.rel = 'preload';
      link.as = 'image';
      link.href = imageSrc;
      link.setAttribute('data-media-preload', 'true');
      document.head.appendChild(link);
    }
  });
}
